/**
 * The contract list a Scenario can start from: a CSV with exactly three columns, Contract Number, Contract Schedule Number and Investor Code
 * (the investor code the contract is under TODAY). A row names one contract schedule by its Contract Number and Contract Schedule Number;
 * the server joins them into the Contract Schedule ("{Contract Number}_{Schedule Number}", e.g. 20086710_1), which is what it matches on,
 * since one contract can carry several schedules. Nobody builds that in the file. Parsing happens in the browser; the rows are then sent to
 * the server, which builds each Contract Schedule, looks it up and reports the ones it cannot accept.
 */
export interface UploadRow {
  contractNumber: string;
  /** the Schedule Number as written in the file ("1"); the server joins it to the Contract Number */
  contractScheduleNumber: string;
  investorCode: string;
  /** only for a file from before the Schedule Number column, which carried the joined Contract Schedule ("20086710_1") instead */
  contractScheduleKey?: string;
}

export const TEMPLATE_HEADERS = ["Contract Number", "Contract Schedule Number", "Investor Code"] as const;

/** The template the user downloads and fills in: the header and one example row. */
export function templateCsv(): string {
  return `${TEMPLATE_HEADERS.join(",")}\r\n1234567,1,10\r\n`;
}

export function downloadTemplate(): void {
  const blob = new Blob([templateCsv()], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "scenario-contracts-template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** Splits one CSV line, honouring double quotes ("a,b" is one cell, "" inside quotes is a quote). */
function splitLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === delimiter) {
      cells.push(cell);
      cell = "";
    } else {
      cell += ch;
    }
  }
  cells.push(cell);
  return cells.map((c) => c.trim());
}

const normalise = (h: string) => h.toLowerCase().replace(/[^a-z]/g, "");

/**
 * Reads the uploaded file's text. The first row must be the header (Contract Number, Contract Schedule Number, Investor Code, in any order,
 * any capitalisation; "Schedule Number" is read as Contract Schedule Number); blank lines are skipped. The rows carry the Schedule Number
 * as it is: the server joins it to the Contract Number. A file from before that column, with the joined Contract Schedule
 * ("Contract Schedule" or contract_schedule_key, like 20086710_1) in place of the Schedule Number, is still read. Throws a readable Error
 * when the columns are not there, so the page can show it.
 */
export function parseContractCsv(text: string): UploadRow[] {
  const lines = text.replace(/^﻿/, "").split(/\r\n|\n|\r/).filter((l) => l.trim() !== "");
  if (lines.length === 0) throw new Error(`The file is empty. Use the template: ${TEMPLATE_HEADERS.join(", ")}.`);
  // Excel in some locales saves with ';' (or a tab) instead of ','; a result copied out of a database client is tab-separated
  const first = lines[0];
  const delimiter = [",", ";", "\t"].sort((a, b) => first.split(b).length - first.split(a).length)[0];
  const header = splitLine(first, delimiter).map(normalise);
  const numberAt = header.indexOf("contractnumber");
  const keyAt = header.findIndex((h) => h === "contractschedulekey" || h === "contractschedule");
  const scheduleAt = header.findIndex((h) => h === "contractschedulenumber" || h === "schedulenumber");
  const codeAt = header.indexOf("investorcode");
  if (numberAt < 0 || (keyAt < 0 && scheduleAt < 0) || codeAt < 0) {
    throw new Error(`The first row must be the header "${TEMPLATE_HEADERS.join(", ")}". Download the template and fill that in.`);
  }
  return lines.slice(1).map((line) => {
    const cells = splitLine(line, delimiter);
    const row: UploadRow = {
      contractNumber: cells[numberAt] ?? "",
      contractScheduleNumber: scheduleAt >= 0 ? (cells[scheduleAt] ?? "") : "",
      investorCode: cells[codeAt] ?? "",
    };
    // the joined Contract Schedule only travels when the file has no Schedule Number column (the older layout)
    if (scheduleAt < 0 && keyAt >= 0) row.contractScheduleKey = cells[keyAt] ?? "";
    return row;
  });
}
